import { TFunction } from 'i18next';
import { isNil } from 'ramda';
import { CardType } from '~client/model/card-type';
import { emptyString } from '~common/helper/string';
import {
  alwaysValid,
  containsAtLeastOneDigitAndLetter,
  isFutureDate,
  isGreaterThanZero,
  isMaxAgeRespected,
  isMinAgeRespected,
  isMinLength,
  isNilOrGreaterThanZero,
  isNotBlank,
  isNotEmpty,
  isNotEqual,
  isNotNegative,
  isValidBirthday,
  isValidCanadianPostalCode,
  isValidCreditCardCVC,
  isValidCreditCardExpirationDate,
  isValidCreditCardNumber,
  isValidCVCByCardType,
  isValidEmail,
  isValidDateBefore,
  isValidDateAfter,
  isValidUrlPartial,
  isValidUSPostalCode,
  Rule,
  ValidationHandler,
} from '~common/validator/validation-rules';
import { t } from '~web/i18n';

export const required = (t?: TFunction, errorMessageKey?: string): Rule<any> => ({
  handler: isNotEmpty,
  message: (): string => {
    if (isNil(t) && isNil(errorMessageKey)) {
      // If the field is left empty and the user navigates away, just make the field red.
      return emptyString;
    } else {
      return t(errorMessageKey);
    }
  },
});

export const checkboxChecked = (): Rule<boolean> => ({
  handler: (value): boolean => value,
  message: (): string => emptyString,
});

export const isNotEqualValidation = (otherValue: string): Rule<string> => ({
  handler: isNotEqual(otherValue),
  message: (): string => emptyString,
});

export const emailValidation: Rule<string> = {
  handler: isValidEmail,
  message: (): string => t('form.validation.error.enterValidEmail'),
};

export const containsOneDigitAndLetterValidation: Rule<string> = {
  handler: containsAtLeastOneDigitAndLetter,
  message: (): string => t('form.validation.error.containsAtLeastOneNumberAndLetter'),
};

export const minLengthValidation = (minLength: number): Rule<string> => ({
  handler: isMinLength(minLength),
  // Validate but do not display an error since it shown inside the field
  message: (): string => emptyString,
});

export const noCountryPostalCodeValidation: Rule<string> = {
  handler: alwaysValid,
  message: (): string => t('form.validation.error.selectCountry'),
};

export const usPostalCodeValidation: Rule<string> = {
  handler: isValidUSPostalCode,
  message: (): string => t('form.validation.error.enterValidZipCode'),
};

export const canadaPostalCodeValidation: Rule<string> = {
  handler: isValidCanadianPostalCode,
  message: (): string => t('form.validation.error.enterValidPostalCode'),
};

export const validBirthdayValidation: Rule<string> = {
  handler: isValidBirthday,
  message: (): string => t('form.validation.error.enterValidBirthday'),
};

export const urlPartialValidation: Rule<string> = {
  handler: isValidUrlPartial,
  message: (): string => t('form.validation.error.enterValidUrlPartial'),
};

export const minAgeValidation: Rule<string> = {
  handler: isMinAgeRespected,
  message: (): string => t('form.validation.error.minAgeBirthday'),
};

export const maxAgeValidation: Rule<string> = {
  handler: isMaxAgeRespected,
  message: (): string => t('form.validation.error.enterValidBirthday'),
};

export const agreementValidation: Rule<Array<string>> = {
  handler: (value: Array<string>) => value !== undefined && Boolean(value.length),
  message: (): string => t('form.validation.error.agree'),
};

export const creditCardExpirationDateValidation: Rule<string> = {
  handler: isValidCreditCardExpirationDate,
  message: (): string => t('form.validation.error.enterValidCreditCardExpiry'),
};

export const creditCardNumberValidation: Rule<string> = {
  handler: isValidCreditCardNumber,
  message: (): string => t('form.validation.error.enterValidCreditCardNumber'),
};

export const creditCardCVCValidation = (creditCardNumber: string): Rule<string> => ({
  handler: isValidCreditCardCVC(creditCardNumber),
  message: (): string => t('form.validation.error.enterValidCVC'),
});

export const creditCardCVCValidationByCardType = (cardType: CardType): Rule<string> => ({
  handler: isValidCVCByCardType(cardType),
  message: (): string => t('form.validation.error.enterValidCVC'),
});

export const postalCodeValidation = (country: string): Rule<string> => {
  switch (country) {
    case 'CA':
      return canadaPostalCodeValidation;
    case 'US':
      return usPostalCodeValidation;
    default:
      return noCountryPostalCodeValidation;
  }
};

export const isNotBlankValidation = (message?: string): Rule<string> => ({
  handler: isNotBlank,
  message: (): string => message ?? emptyString,
});

export const greaterThanZeroNumberValidation = (message?: string): Rule<number> => ({
  handler: isGreaterThanZero,
  message: (): string => message ?? emptyString,
});

export const notNegativeNumberValidation = (message?: string): Rule<number> => ({
  handler: isNotNegative,
  message: (): string => message ?? emptyString,
});

export const nilOrGreaterThanZeroNumberValidation = (message?: string): Rule<number> => ({
  handler: isNilOrGreaterThanZero,
  message: (): string => message ?? emptyString,
});

export const after = (endDateTime: string): Rule<string> => ({
  handler: isValidDateAfter(endDateTime),
  message: (): string => emptyString,
});

export const before = (startDateTime: string): Rule<string> => ({
  handler: isValidDateBefore(startDateTime),
  message: (): string => emptyString,
});

export const futureDateValidation = (): Rule<string> => ({
  handler: isFutureDate,
  message: (): string => emptyString,
});

export const customValidation = (handler: ValidationHandler<string>): Rule<string> => ({
  handler: handler,
  message: (): string => emptyString,
});



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/common/src/validator/validations.ts